import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Boxes, ClipboardCheck, Warehouse } from "lucide-react";

import OpenWorkPanel from "../../components/OpenWorkPanel";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader } from "../../components/erp";
import { inventoryAPI, logisticsAPI } from "../../services/businessErpService";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function movementDelta(row) {
  return Number(
    row?.quantity_change ??
    row?.quantity_delta ??
    row?.delta_quantity ??
    row?.quantity ??
    0,
  );
}

export default function InventoryDashboard() {
  const [stockLevels, setStockLevels] = useState([]);
  const [movements, setMovements] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [levelsRes, movementsRes, deliveriesRes] = await Promise.all([
        inventoryAPI.listStockLevels({ limit: 200 }),
        inventoryAPI.listMovements({ limit: 8 }),
        logisticsAPI.list({ limit: 100 }),
      ]);
      setStockLevels(Array.isArray(levelsRes?.data) ? levelsRes.data : []);
      setMovements(Array.isArray(movementsRes?.data) ? movementsRes.data : Array.isArray(movementsRes?.items) ? movementsRes.items : []);
      setDeliveries(Array.isArray(deliveriesRes?.data?.deliveries) ? deliveriesRes.data.deliveries : Array.isArray(deliveriesRes?.data) ? deliveriesRes.data : []);
    } catch {
      setStockLevels([]);
      setMovements([]);
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const lowStock = stockLevels.filter((row) => Number(row.quantity ?? 0) > 0 && Number(row.quantity ?? 0) <= Number(row.low_stock_threshold ?? 5));
    const outOfStock = stockLevels.filter((row) => Number(row.quantity ?? 0) <= 0);
    const pendingReceipts = deliveries.filter((row) =>
      ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(String(row.receiving_status || "").toUpperCase()),
    );
    const discrepancyReceipts = pendingReceipts.filter((row) => String(row.receiving_status || "").toUpperCase() === "RECEIVED_WITH_DISCREPANCY");
    return {
      trackedVariants: stockLevels.length,
      onHand: stockLevels.reduce((sum, row) => sum + Number(row.quantity || 0), 0),
      lowStock,
      outOfStock,
      pendingReceipts,
      discrepancyReceipts,
      openWorkItems: [
        lowStock.length ? {
          id: "inventory-low-stock",
          title: "Low-stock variants",
          description: "Variants are below their reorder threshold and need replenishment planning.",
          count: lowStock.length,
          tone: "amber",
          href: "/business/inventory/open-work?lane=lowStock",
          actionLabel: "Open queue",
        } : null,
        outOfStock.length ? {
          id: "inventory-out-of-stock",
          title: "Out-of-stock variants",
          description: "Variants have no on-hand inventory and need urgent follow-through.",
          count: outOfStock.length,
          tone: "rose",
          href: "/business/inventory/open-work?lane=outOfStock",
          actionLabel: "Open queue",
        } : null,
        pendingReceipts.length ? {
          id: "inventory-receipts",
          title: "Delivered shipments awaiting receipt",
          description: "Inventory is not complete until the business records receipt.",
          count: pendingReceipts.length,
          tone: "blue",
          href: "/business/inventory/open-work?lane=pendingReceipts",
          actionLabel: "Open queue",
        } : null,
        discrepancyReceipts.length ? {
          id: "inventory-discrepancy",
          title: "Receipts with discrepancy",
          description: "Received shipments still have rejected or discrepant quantities to resolve.",
          count: discrepancyReceipts.length,
          tone: "rose",
          href: "/business/inventory/open-work?lane=discrepancies",
          actionLabel: "Open queue",
        } : null,
      ].filter(Boolean),
    };
  }, [deliveries, stockLevels]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Inventory"
        title="Inventory Dashboard"
        subtitle="Use inventory as the operational command center for stock health, incoming receipt work, and recent stock movement."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Tracked Variants" value={loading ? "—" : summary.trackedVariants} icon={Boxes} />
        <StatCard title="On-hand Quantity" value={loading ? "—" : summary.onHand} icon={Warehouse} />
        <StatCard title="Low Stock" value={loading ? "—" : summary.lowStock.length} icon={AlertTriangle} accent />
        <StatCard title="Pending Receipts" value={loading ? "—" : summary.pendingReceipts.length} icon={ClipboardCheck} accent />
      </div>

      <OpenWorkPanel
        title="Inventory Open Work Snapshot"
        subtitle="Inventory exceptions and receipt tasks that still need attention."
        items={loading ? [] : summary.openWorkItems}
        emptyTitle="Inventory queue is clear"
        emptyDescription="No stock health or receiving blockers are currently open."
      />

      <Panel
          title="Recent Inventory Movements"
          subtitle="Latest posted inventory transactions"
          right={
            <Link
              to="/business/inventory"
              className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
            >
              Stock overview <ArrowRight className="h-4 w-4" />
            </Link>
          }
        >
          {loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading inventory movements…</div>
          ) : !movements.length ? (
            <EmptyState icon={Boxes} title="No movements yet" description="Inventory movement history will appear here as stock changes are posted." />
          ) : (
            <div className="space-y-3">
              {movements.slice(0, 8).map((row) => (
                <div key={row.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {row.product_name || "Product"} · {row.variant_title || "Default"}
                      </div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {row.movement_type || "Movement"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${movementDelta(row) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                        {movementDelta(row) >= 0 ? "+" : ""}{movementDelta(row)}
                      </div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {formatDateTime(row.created_at || row.createdAt || row.recorded_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Panel>
    </div>
  );
}
